/// <reference types="applepayjs" />
import { applePayButtonTypesArr } from './types';
import type { ApplePayButtonType } from './types';

// Apple's "latest v1" Apple Pay JS SDK. It registers the <apple-pay-button>
// custom element and keeps the ApplePaySession API current.
const APPLE_PAY_SDK_URL = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';

const APPLE_PAY_BUTTON_TAG = 'apple-pay-button';

// Custom elements register per-document, so — like Google Pay's pay.js — the SDK
// must load into the same document as the button; track the loader per document.
const sdkPromises = new WeakMap<Document, Promise<void>>();

export const loadApplePaySdk = (doc: Document): Promise<void> => {
  if (doc.defaultView?.customElements.get(APPLE_PAY_BUTTON_TAG)) {
    return Promise.resolve();
  }

  let promise = sdkPromises.get(doc);

  if (!promise) {
    promise = new Promise<void>((resolve, reject) => {
      const script = doc.createElement('script');
      script.src = APPLE_PAY_SDK_URL;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.addEventListener('load', () => {
        resolve();
      });
      script.addEventListener('error', () => {
        sdkPromises.delete(doc);
        script.remove();
        reject(new Error(`Unable to load script ${APPLE_PAY_SDK_URL}`));
      });
      doc.head.appendChild(script);
    });
    sdkPromises.set(doc, promise);
  }

  return promise;
};

export const DEFAULT_APPLE_PAY_BUTTON_TYPE: ApplePayButtonType = 'plain';

/** Fallback network list (mirrors the hosted PayPage default). */
export const DEFAULT_APPLE_PAY_CARD_NETWORKS = ['visa', 'masterCard', 'amex', 'discover', 'jcb'];

// Apple Pay's `supportedNetworks` identifiers are camelCase and case-sensitive:
// an unrecognised value (e.g. a blanket-lowercased `mastercard`) is dropped, so
// those cards silently never appear on the sheet. Map the configured card brands
// (any case) onto the canonical ids; unknown brands fall back to lowercase.
const APPLE_PAY_NETWORK_BY_BRAND: Record<string, string> = {
  visa: 'visa',
  mastercard: 'masterCard',
  amex: 'amex',
  discover: 'discover',
  jcb: 'jcb',
};

export const toApplePayNetwork = (brand: string): string => APPLE_PAY_NETWORK_BY_BRAND[brand.toLowerCase()] ?? brand.toLowerCase();

export const isValidApplePayButtonType = (value: string | null | undefined): value is ApplePayButtonType =>
  !!value && applePayButtonTypesArr.includes(value as ApplePayButtonType);

export const resolveApplePayButtonType = (...candidates: (string | null | undefined)[]): ApplePayButtonType =>
  candidates.find(isValidApplePayButtonType) ?? DEFAULT_APPLE_PAY_BUTTON_TYPE;

type MaybeApplePayWindow = { ApplePaySession?: typeof ApplePaySession };

export const getApplePayWindow = (window: Window): Window | null => {
  let current = window;

  while (current.location.origin === 'null') {
    if (current === current.parent) {
      return null;
    }

    current = current.parent;
  }

  return current;
};

const resolveApplePaySession = (view: Window | null | undefined): typeof ApplePaySession | undefined =>
  (view as unknown as MaybeApplePayWindow | null | undefined)?.ApplePaySession;

/**
 * Whether Apple Pay may even be attempted here: a secure context. Used to decide
 * whether to load the SDK before `ApplePaySession` exists.
 *
 * `isSecureContext` rather than a `location.protocol === 'https:'` check, because it is
 * also true on `http://localhost` for local dev. Apple itself judges the document URL and
 * will refuse a non-`https:` one — that is what `canMakePayments()` below reports, and it
 * is why OneClick must not be rendered into a `srcdoc`/`about:blank` frame.
 */
export const isSecureApplePayContext = (view: Window = window): boolean => {
  try {
    return view.isSecureContext;
  } catch {
    return false;
  }
};

/**
 * Apple Pay availability. Realm-aware: pass the button's `ownerDocument.defaultView` so
 * the check runs in the document that actually hosts the button. Requires a secure context,
 * `ApplePaySession`, and `canMakePayments()`. No browser sniffing — the Apple Pay JS SDK
 * exposes `ApplePaySession` in every browser it supports (Safari plus Chrome/Edge and
 * cross-device desktop browsers), so `canMakePayments()` is the source of truth.
 *
 * `canMakePayments()` *throws* rather than returning `false` when the document URL is one
 * Apple rejects, so the `catch` is load-bearing. Note the SDK polyfill throws a **bare
 * string**, not an `Error`, so never inspect `.name`/`.message` on what lands there.
 */
export const getCanMakeApplePayPayments = (view: Window = window): boolean => {
  try {
    const Session = resolveApplePaySession(view);
    return !!Session && Session.canMakePayments();
  } catch {
    return false;
  }
};

/**
 * The domain Apple validates the merchant session against: the hostname of the document
 * that **creates** the session, and deliberately NOT the address-bar domain.
 *
 * Apple's DTS guidance: "the parent page domain is the one registered in the Developer
 * portal, and used in the request to generate a merchant session via ApplePaySession".
 * The registered domain is the innermost page that owns the Apple Pay code (ours), so
 * when that page is itself embedded in an integrator's site, walking out to `top` would
 * send the *integrator's* hostname — which Apple has no registration for, and validation
 * fails.
 */
export const getApplePayMerchantDomain = (view: Window = window): string => {
  try {
    return view.location.hostname;
  } catch {
    return '';
  }
};
